import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flame,
  CheckCircle2,
  Circle,
  UtensilsCrossed,
  ChefHat,
  Scale,
} from "lucide-react";
import { useTimeFormatter } from "@/lib/timeFormat";

export default function MealDetailModal({
  open,
  onClose,
  meal,
  mealIndex = 0,
  totalMeals = 1,
  isCompleted = false,
  onToggleLog,
  onPrevMeal,
  onNextMeal,
  isLogging = false,
}) {
  const fmtTime = useTimeFormatter();

  if (!open || !meal) return null;

  const foods = Array.isArray(meal.foods) ? meal.foods : [];
  const totalCals = foods.reduce((s, f) => s + (Number(f.calories) || 0), 0);
  const totalProtein = Math.round(foods.reduce((s, f) => s + (Number(f.protein_g) || 0), 0));
  const totalCarbs = Math.round(foods.reduce((s, f) => s + (Number(f.carbs_g) || 0), 0));
  const totalFat = Math.round(foods.reduce((s, f) => s + (Number(f.fat_g || f.fats_g) || 0), 0));

  // Extract instructions (could be string or array)
  let instructions = [];
  if (Array.isArray(meal.instructions)) {
    instructions = meal.instructions;
  } else if (typeof meal.instructions === "string" && meal.instructions.trim()) {
    instructions = meal.instructions
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (typeof meal.recipe === "string" && meal.recipe.trim()) {
    instructions = meal.recipe
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  } else if (typeof meal.notes === "string" && meal.notes.trim()) {
    instructions = [meal.notes.trim()];
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-[#030A17] flex flex-col overflow-y-auto text-white animate-in fade-in duration-200"
      data-testid="meal-detail-modal"
    >
      {/* Top Header */}
      <div className="sticky top-0 z-20 bg-[#030A17]/95 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 text-sky-400" />
          <span className="text-xs font-semibold">Back</span>
        </button>

        <div className="text-center">
          <p className="text-[10px] font-bold text-sky-400 uppercase tracking-widest">
            Meal {mealIndex + 1} of {totalMeals}
          </p>
          <h2 className="text-xs font-bold text-white truncate max-w-[180px]">
            {meal.meal_name}
          </h2>
        </div>

        {/* Previous / Next Meal Controls */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrevMeal}
            disabled={mealIndex === 0}
            className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
            title="Previous Meal"
            aria-label="Previous Meal"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onNextMeal}
            disabled={mealIndex >= totalMeals - 1}
            className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
            title="Next Meal"
            aria-label="Next Meal"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full max-w-2xl mx-auto px-4 py-4 space-y-4 pb-28">
        <HealthSourceNote topic="food" />
        {/* Food Hero Image / Branded Placeholder */}
        <div className="relative w-full h-48 sm:h-56 rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B1733] to-[#040C1A] flex items-center justify-center shadow-lg">
          {meal.image_url ? (
            <img
              src={meal.image_url}
              alt={meal.meal_name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto text-sky-400">
                <UtensilsCrossed className="w-7 h-7" />
              </div>
              <p className="text-xs font-bold text-white tracking-wide">
                TA FITNESS NUTRITION
              </p>
            </div>
          )}

          {/* Scheduled Time Pill Overlay */}
          {meal.time && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md border border-white/10 text-xs font-semibold text-white flex items-center gap-1.5 shadow-sm">
              <Clock className="w-3.5 h-3.5 text-sky-400" />
              <span>{fmtTime(meal.time)}</span>
            </div>
          )}

          {/* Logged Status Badge Overlay */}
          {isCompleted && (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-emerald-500/90 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-md">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Logged</span>
            </div>
          )}
        </div>

        {/* 4 Nutrition Macro Statistics */}
        <div className="grid grid-cols-4 gap-2">
          <div className="p-3 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-0.5">
            <span className="text-[10px] font-bold text-orange-400 uppercase tracking-wider block">
              Calories
            </span>
            <p className="text-sm font-extrabold text-white">{totalCals}</p>
            <span className="text-[9px] text-slate-400 block">kcal</span>
          </div>

          <div className="p-3 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-0.5">
            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider block">
              Protein
            </span>
            <p className="text-sm font-extrabold text-white">{totalProtein}g</p>
            <span className="text-[9px] text-slate-400 block">macros</span>
          </div>

          <div className="p-3 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-0.5">
            <span className="text-[10px] font-bold text-sky-400 uppercase tracking-wider block">
              Carbs
            </span>
            <p className="text-sm font-extrabold text-white">{totalCarbs}g</p>
            <span className="text-[9px] text-slate-400 block">macros</span>
          </div>

          <div className="p-3 rounded-2xl bg-[#071326] border border-white/10 text-center space-y-0.5">
            <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider block">
              Fat
            </span>
            <p className="text-sm font-extrabold text-white">{totalFat}g</p>
            <span className="text-[9px] text-slate-400 block">macros</span>
          </div>
        </div>

        {/* Ingredients & Portions */}
        <div className="p-4 rounded-2xl bg-[#071326] border border-white/10 space-y-3">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-sky-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-white">
              Ingredients & Portions
            </h3>
          </div>

          {foods.length === 0 ? (
            <p className="text-xs text-slate-400">No specific ingredients listed.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {foods.map((food, fIdx) => {
                const portionText = food.portion || (food.quantity && food.unit ? `${food.quantity} ${food.unit}` : food.quantity || "");

                return (
                  <div key={fIdx} className="py-2.5 flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-bold text-white capitalize">{food.name}</p>
                      {portionText && (
                        <p className="text-[11px] text-slate-400 mt-0.5">Portion: {portionText}</p>
                      )}
                    </div>
                    <div className="text-right">
                      {food.calories && (
                        <span className="text-xs font-bold text-orange-400 block">
                          {food.calories} kcal
                        </span>
                      )}
                      {(food.protein_g || food.carbs_g || food.fat_g) && (
                        <span className="text-[10px] text-slate-400 block">
                          P:{food.protein_g || 0}g · C:{food.carbs_g || 0}g · F:{food.fat_g || 0}g
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Preparation Instructions */}
        <div className="p-4 rounded-2xl bg-[#071326] border border-white/10 space-y-3">
          <div className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-sky-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-white">
              Preparation Instructions
            </h3>
          </div>

          {instructions.length === 0 ? (
            <p className="text-xs text-slate-400">
              No specific preparation instructions required for this meal.
            </p>
          ) : (
            <div className="space-y-2">
              {instructions.map((step, idx) => (
                <div key={idx} className="flex items-start gap-3 text-xs text-slate-300">
                  <span className="w-5 h-5 rounded-full bg-white/5 border border-white/10 text-sky-400 font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <p className="flex-1 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Action Dock */}
      <div className="fixed bottom-0 left-0 right-0 z-30 p-4 bg-[#030A17]/95 backdrop-blur-md border-t border-white/10">
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={onToggleLog}
            disabled={isLogging}
            data-testid="mark-meal-logged-btn"
            className={`w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition active:scale-[0.98] cursor-pointer shadow-lg ${
              isCompleted
                ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-emerald-500/20"
                : "bg-gradient-to-r from-sky-500 to-cyan-400 hover:from-sky-400 hover:to-cyan-300 text-slate-950 shadow-sky-500/25"
            }`}
          >
            {isCompleted ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Logged (Tap to Undo)</span>
              </>
            ) : (
              <>
                <Circle className="w-5 h-5" />
                <span>Mark as Logged</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
